import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import { TextAreaField } from '@app/components/form-fields/TextAreaField';
import { TextField } from '@app/components/form-fields/TextField';
import useAgentTypes from '@app/hooks/agents/useAgentTypes';
import {
    liveAgentVoices,
    realtimeAgentVoices,
    voiceAgentModels,
    voiceBackendAgentModels,
} from '@app/utils/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flex } from 'antd';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import { AgentVoicePreview } from './AgentVoicePreview';
import { AssociatedDocumentsField } from './AssociatedDocumentsField';

const schema = z
    .object({
        name: z.string().trim().min(1, 'Name is required'),
        type_id: z.string('Type is required'),
        model_name: z.enum(voiceAgentModels, 'Model is required'),
        voice: z.enum([...realtimeAgentVoices, ...liveAgentVoices], 'Voice is required'),
        prompt: z.string().trim().optional(),
        frontend_instructions: z.string().trim().optional(),
        backend_model: z.enum(voiceBackendAgentModels).optional(),
        backend_instructions: z.string().trim().optional(),
    })
    .superRefine((values, ctx) => {
        if (values.model_name === 'gpt-live-1') {
            if (!values.frontend_instructions) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Frontend Instruction is required',
                    path: ['frontend_instructions'],
                });
            }
            if (!values.backend_model) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Backend Model is required',
                    path: ['backend_model'],
                });
            }
            if (!values.backend_instructions) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'Backend Instruction is required',
                    path: ['backend_instructions'],
                });
            }
        } else if (!values.prompt) {
            ctx.addIssue({
                code: 'custom',
                message: 'Prompt is required',
                path: ['prompt'],
            });
        }
    })
    .transform((values) => {
        const submitValues = { ...values };
        if (submitValues.model_name === 'gpt-live-1') {
            delete submitValues.prompt;
        } else {
            delete submitValues.frontend_instructions;
            delete submitValues.backend_model;
            delete submitValues.backend_instructions;
        }
        return submitValues;
    });

export type AgentFormValues = z.infer<typeof schema>;

export type AgentFormProps = {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    loading?: boolean;
    onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onSubmit: (data: AgentFormValues, reset: UseFormReset<AgentFormValues>) => void;
};

export const AgentForm = ({ onSubmit, onCancel, data, ...props }: AgentFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { isValid, isSubmitting },
        watch,
    } = useForm<AgentFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            name: '',
            model_name: undefined,
            type_id: undefined,
            voice: undefined,
            prompt: '',
            backend_model: undefined,
            backend_instructions: '',
            frontend_instructions: '',
        },
    });

    const { data: agentTypes } = useAgentTypes();
    const agentTypeOptions =
        agentTypes?.map((item) => ({
            label: item.name,
            value: item.id,
        })) || [];

    const agentModelOptions = voiceAgentModels.map((voice_id) => ({
        label: voice_id,
        value: voice_id,
    }));
    const backendModelOptions = voiceBackendAgentModels.map((voice_id) => ({
        label: voice_id,
        value: voice_id,
    }));

    const selectedVoice = watch('voice');
    const selectedModel = watch('model_name');

    const voiceIds = selectedModel === 'gpt-live-1' ? liveAgentVoices : realtimeAgentVoices;
    const agentVoiceOptions = voiceIds.map((voice_id) => ({
        label: voice_id,
        value: voice_id,
    }));

    useEffect(() => {
        reset({
            ...data,
            model_name: data?.model_name || undefined,
            voice: data?.voice || undefined,
            prompt: data?.prompt || '',
            backend_model: data?.backend_model || undefined,
            backend_instructions: data?.backend_instructions || '',
            frontend_instructions: data?.frontend_instructions || '',
        });
    }, [data, reset]);

    return (
        <form onSubmit={handleSubmit((values) => onSubmit(values, reset))} id={props.id}>
            <div className="form-fields-wrapper">
                <div className="grid">
                    <div className="grid-xs-12">
                        <Controller
                            control={control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Name"
                                    type="text"
                                    placeholder="Enter name"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="type_id"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Type"
                                    placeholder="Select type"
                                    options={agentTypeOptions}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="model_name"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    onChange={(value, option?) => {
                                        field.onChange(value, option);
                                        // reset the selected voice to null
                                        setValue(
                                            'voice',
                                            null as unknown as AgentFormValues['voice']
                                        );
                                    }}
                                    label="Model"
                                    placeholder="Select model"
                                    options={agentModelOptions}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="voice"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    label={
                                        <Flex justify="space-between" gap={20}>
                                            <span>Voice</span>
                                            <AgentVoicePreview
                                                voice={selectedVoice}
                                                model={selectedModel}
                                            />
                                        </Flex>
                                    }
                                    placeholder="Select voice"
                                    options={agentVoiceOptions}
                                />
                            )}
                        />
                        {selectedModel === 'gpt-live-1' ? (
                            <>
                                <Controller
                                    control={control}
                                    name="frontend_instructions"
                                    render={({ field, fieldState }) => (
                                        <TextAreaField
                                            fieldState={fieldState}
                                            {...field}
                                            label={
                                                <Flex justify="space-between" gap={20}>
                                                    <span>Frontend Instructions</span>
                                                    <a
                                                        href="https://developers.openai.com/api/docs/guides/live-prompting"
                                                        target="_blank"
                                                        className="link">
                                                        <small>View Docs</small>
                                                    </a>
                                                </Flex>
                                            }
                                            placeholder="Enter prompt"
                                            rows={6}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="backend_model"
                                    render={({ field, fieldState }) => (
                                        <SelectField
                                            fieldState={fieldState}
                                            {...field}
                                            label="Backend Model"
                                            placeholder="Select model"
                                            options={backendModelOptions}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="backend_instructions"
                                    render={({ field, fieldState }) => (
                                        <TextAreaField
                                            fieldState={fieldState}
                                            {...field}
                                            label={
                                                <Flex justify="space-between" gap={20}>
                                                    <span>Backend Instructions</span>
                                                    <a
                                                        href="https://developers.openai.com/api/docs/guides/live-prompting"
                                                        target="_blank"
                                                        className="link">
                                                        <small>View Docs</small>
                                                    </a>
                                                </Flex>
                                            }
                                            placeholder="Enter prompt"
                                            rows={6}
                                        />
                                    )}
                                />
                                <AssociatedDocumentsField agentId={data?.id} />
                            </>
                        ) : (
                            <Controller
                                control={control}
                                name="prompt"
                                render={({ field, fieldState }) => (
                                    <TextAreaField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Prompt"
                                        placeholder="Enter prompt"
                                        rows={8}
                                    />
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="add-agent-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
